import React, { useEffect, useRef, useState } from "react";
import { Rnd } from "react-rnd";

import { selectNetworkDepthLegend, useMapStore } from "stores/map";
import styled from "styled-components";

import { useLocalStorage } from "hooks";

import { useScreenshotContext } from "components/screenshot/hooks";
import { getLegendItemSize } from "components/screenshot/utils";
import {
  LegendDimensionsT,
  LegendPositionT,
  LegendT
} from "components/shared/charts/legend/Legend";
import getCalculatedLegend from "components/shared/charts/legend/utils/getCalculatedLegend";
import {
  LegendPosition,
  getPositionFromChartSettings
} from "components/shared/charts/legend/utils/getPositionFromChartSettings";

const dimensionsStorageKey = `map-screenshot-network-depth-legend-dimensions`;
const positionStorageKey = `map-screenshot-network-depth-legend-position`;

type NetworkDepthLegendWrapperT = {
  isScreenshotOverlayVisible: boolean;
  defaultPosition?: LegendPosition;
};

export const NetworkDepthLegendWrapper: React.FC<NetworkDepthLegendWrapperT> = ({
  isScreenshotOverlayVisible,
  defaultPosition = LegendPosition.SW
}) => {
  // Hooks
  const { settings } = useScreenshotContext();
  const networkDepthLegend = useMapStore(selectNetworkDepthLegend);

  const { getStoredValue: getStoredDimensions } = useLocalStorage(
    dimensionsStorageKey,
    undefined
  );

  const { getStoredValue: getStoredPosition } = useLocalStorage(
    positionStorageKey,
    undefined
  );

  const rndRef = useRef<Rnd>(null);

  const mapScreenshotScale = isNaN(Number(settings?.mapScreenshotScale))
    ? 1
    : Number(settings?.mapScreenshotScale);

  const screenshotBounds = {
    width: (settings?.width || 1152) * (settings?.mapScreenshotScale ?? 1),
    height: (settings?.height || 681) * (settings?.mapScreenshotScale ?? 1)
  };
  const backgroundOpacity = isScreenshotOverlayVisible
    ? Number(settings?.networkDepthLegendOpacity) ?? 1
    : 1;
  const labelsSize = isScreenshotOverlayVisible
    ? Number(settings?.networkDepthLabelsSize ?? settings?.legendFontSize ?? 14) *
      mapScreenshotScale
    : 14;
  const titleSize = isScreenshotOverlayVisible
    ? Number(settings?.networkDepthTitleSize ?? settings?.legendTitleSize ?? 15) *
      mapScreenshotScale
    : 15;

  const [dimensions, setDimensions] = useState<LegendDimensionsT>(() => {
    const itemSize = getLegendItemSize(isScreenshotOverlayVisible, labelsSize);
    const itemsHeight = networkDepthLegend.legendItems.length * itemSize;

    const storedDimensions = getStoredDimensions();
    return storedDimensions
      ? storedDimensions
      : { width: 180, height: itemsHeight ? itemsHeight + 40 : 180 }; // 40px for title
  });

  // Update dimensions when legend items change
  useEffect(() => {
    const itemSize = getLegendItemSize(isScreenshotOverlayVisible, labelsSize);
    const itemsHeight = networkDepthLegend.legendItems.length * itemSize;
    const newHeight = itemsHeight ? itemsHeight + 40 : 180;

    // Only update if dimensions have significantly changed and no stored dimensions exist
    if (!getStoredDimensions() && Math.abs(dimensions.height - newHeight) > 20) {
      setDimensions((prev) => ({ ...prev, height: newHeight }));
    }
  }, [networkDepthLegend.legendItems.length, isScreenshotOverlayVisible, labelsSize]);

  const [position, setPosition] = useState<LegendPositionT>(() => {
    const storedPosition = getStoredPosition();
    return storedPosition
      ? storedPosition
      : getPositionFromChartSettings(dimensions, screenshotBounds, defaultPosition);
  });

  const handleDragStop = (_, dragElement) => {
    const newPosition = {
      x: dragElement.x,
      y: dragElement.y
    };

    setPosition(newPosition);
    localStorage.setItem(positionStorageKey, JSON.stringify(newPosition));
  };

  const handleResize = (e, direction, ref, delta, p) => {
    const nextDimensions = {
      width: ref.offsetWidth,
      height: ref.offsetHeight
    };
    setDimensions(nextDimensions);
    setPosition(p);

    localStorage.setItem(dimensionsStorageKey, JSON.stringify(nextDimensions));
    localStorage.setItem(positionStorageKey, JSON.stringify(p));
  };

  const currentLegend: LegendT = {
    ...position,
    ...dimensions
  };

  const calculatedLegend = getCalculatedLegend(currentLegend, screenshotBounds);
  const storedPosition = getStoredPosition();
  const currentPosition = storedPosition
    ? { x: calculatedLegend.x, y: calculatedLegend.y }
    : getPositionFromChartSettings(
        { width: calculatedLegend.width, height: calculatedLegend.height },
        screenshotBounds,
        defaultPosition
      );

  useEffect(() => {
    if (rndRef.current) {
      rndRef.current.updatePosition(currentPosition);
    }
  }, [currentPosition.x, currentPosition.y]);

  // In screenshot mode, show legend if the screenshot setting is enabled and there are legend items
  // In regular mode, follow the networkDepthLegend.visible state
  const shouldShowLegend = isScreenshotOverlayVisible
    ? settings?.showNetworkDepthLegendInScreenshot &&
      networkDepthLegend.legendItems.length > 0
    : networkDepthLegend.visible && settings?.showNetworkDepthLegendInScreenshot;

  if (!shouldShowLegend) {
    return null;
  }

  return (
    <StyledRnd
      bounds="parent"
      position={{ x: currentPosition.x, y: currentPosition.y }}
      size={{ width: calculatedLegend.width, height: calculatedLegend.height }}
      onDragStop={handleDragStop}
      onResize={handleResize}
      $mapScreenshotScale={mapScreenshotScale}
      $showLegendBorder={settings?.showNetworkDepthLegendBorderInScreenshot}
      ref={rndRef}>
      <StyledNetworkDepthLegendWrapper backgroundopacity={backgroundOpacity}>
        <StyledLegendTitle $titleSize={titleSize}>
          {networkDepthLegend.title}
        </StyledLegendTitle>
        <StyledLegendItems>
          {networkDepthLegend.legendItems.map((item, index) => (
            <StyledLegendItem key={index} $labelSize={labelsSize}>
              <StyledColorBox style={{ backgroundColor: item.color }} />
              <StyledLegendText>{item.title}</StyledLegendText>
            </StyledLegendItem>
          ))}
        </StyledLegendItems>
      </StyledNetworkDepthLegendWrapper>
    </StyledRnd>
  );
};

const StyledRnd = styled(Rnd)`
  display: grid;
  overflow: hidden;
  min-height: ${(props: { $mapScreenshotScale: number }) =>
    props?.$mapScreenshotScale * 30 || 30}px;
  min-width: ${(props: { $mapScreenshotScale: number }) =>
    props?.$mapScreenshotScale * 150 || 150}px;
  border: ${(props: { $showLegendBorder: boolean }) =>
    props.$showLegendBorder ? "1px solid gray" : ""};

  & > div:last-child {
    position: unset;
  }

  position: relative;
  z-index: 9999;
`;

const StyledNetworkDepthLegendWrapper = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  display: inline-flex;
  flex-direction: column;
  background: ${(props: { backgroundopacity: number }) =>
    props.backgroundopacity === undefined
      ? "White"
      : `rgba(255, 255, 255, ${props.backgroundopacity})`};
  overflow: hidden auto;
  padding: 8px;
`;

const StyledLegendTitle = styled.div`
  font-size: ${(props: { $titleSize: number }) => props.$titleSize}px;
  font-weight: bold;
  margin-bottom: 8px;
  text-align: left;
`;

const StyledLegendItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${(props: { $labelSize: number }) => props.$labelSize}px;
`;

const StyledColorBox = styled.div`
  width: 16px;
  height: 16px;
  border: 1px solid #ccc;
  flex-shrink: 0;
`;

const StyledLegendText = styled.span`
  flex: 1;
`;
